import React, { useCallback, useRef, useState } from 'react';
import { Camera, Paperclip, FileText, ImageOff } from 'lucide-react';
import SectionShell from '../plan/SectionShell';
import { jobBriefApi } from '../../../services/jobBriefApi';

const MAX_BYTES = 4 * 1024 * 1024;

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      // Strip the "data:...;base64," prefix so the backend gets pure base64.
      const s = String(r.result || '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/**
 * AttachmentsSection — Phase D.
 *
 * File picker → base64 upload → POSTs to /perform/attachment which stores
 * the raw bytes in `job_brief_attachments` and appends a
 * `perform_attachment_added` timeline event. The list of attachments is
 * derived from the append-only timeline via `performState.attachments`.
 *
 * Photos render as inline thumbnails (lazy-fetched); non-image files
 * show a filename + download link.
 */
export default function AttachmentsSection({ draftId, performState, ownerEmail, onChanged, isReadOnly }) {
  const attachments = performState?.attachments || [];
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const chooseFile = useCallback(() => fileRef.current?.click(), []);

  const uploadFile = useCallback(async (file) => {
    if (!file || !draftId) return;
    if (file.size > MAX_BYTES) {
      setError(`File too large — maximum ${Math.floor(MAX_BYTES / 1024 / 1024)} MB.`);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data_base64 = await fileToBase64(file);
      await jobBriefApi.uploadAttachment(draftId, {
        filename:    file.name,
        mime_type:   file.type || 'application/octet-stream',
        data_base64,
        caption:     caption.trim() || undefined,
        uploaded_by: ownerEmail,
      });
      setCaption('');
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || 'Upload failed';
      setError(String(detail));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }, [draftId, ownerEmail, caption, onChanged]);

  return (
    <SectionShell
      icon={Paperclip}
      title="Photos & Attachments"
      subtitle="Snap a photo of the equipment, the tag, or the site condition. Every upload is stamped to the audit log."
      color="#facc15"
      badge={attachments.length ? `${attachments.length} file${attachments.length === 1 ? '' : 's'}` : 'None'}
      testId="jbv2-section-attachments"
    >
      {attachments.length === 0 && (
        <div className="jbv2-empty" data-testid="jbv2-attachments-empty">
          No attachments yet. Add a photo or file below.
        </div>
      )}
      {attachments.length > 0 && (
        <div
          data-testid="jbv2-attachments-list"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}
        >
          {attachments.map((a) => (
            <AttachmentCard key={a.attachment_id} draftId={draftId} attachment={a} />
          ))}
        </div>
      )}

      {!isReadOnly && (
        <div className="jbv2-add-row" style={{ flexDirection: 'column', alignItems: 'stretch' }} data-testid="jbv2-attachment-upload-form">
          <input
            type="text"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Caption (optional)"
            data-testid="jbv2-attachment-caption-input"
            aria-label="Attachment caption"
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              data-testid="jbv2-attachment-file-input"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadFile(f);
              }}
            />
            <button
              type="button"
              onClick={chooseFile}
              disabled={busy}
              data-testid="jbv2-attachment-choose-btn"
            >
              <Camera size={13} /> {busy ? 'Uploading…' : 'Choose photo / file'}
            </button>
          </div>
          {error && (
            <div style={{
              padding: '8px 10px', borderRadius: 8,
              background: 'rgba(220,38,38,0.10)',
              border: '1px solid rgba(220,38,38,0.35)',
              color: '#fca5a5', fontSize: 12,
            }} data-testid="jbv2-attachment-error">
              {error}
            </div>
          )}
        </div>
      )}
    </SectionShell>
  );
}

function AttachmentCard({ draftId, attachment }) {
  const [dataUrl, setDataUrl] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  const isImage = String(attachment.mime_type || '').startsWith('image/');

  React.useEffect(() => {
    let cancelled = false;
    if (!isImage) return () => {};
    jobBriefApi.getAttachment(draftId, attachment.attachment_id)
      .then((d) => { if (!cancelled) setDataUrl(d?.data_url || null); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [draftId, attachment.attachment_id, isImage]);

  return (
    <div
      style={{
        padding: 10, borderRadius: 12,
        background: 'rgba(5,10,31,0.55)',
        border: '1px solid rgba(148,163,184,0.20)',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
      data-testid={`jbv2-attachment-card-${attachment.attachment_id}`}
    >
      {isImage && dataUrl && (
        <img
          src={dataUrl}
          alt={attachment.filename}
          data-testid={`jbv2-attachment-image-${attachment.attachment_id}`}
          style={{
            width: '100%', maxHeight: 180, objectFit: 'cover',
            borderRadius: 8, background: 'rgba(15,27,70,0.85)',
          }}
        />
      )}
      {isImage && !dataUrl && !failed && (
        <div style={{
          height: 140, borderRadius: 8, background: 'rgba(15,27,70,0.65)',
          display: 'grid', placeItems: 'center', color: '#94a3b8', fontSize: 12,
        }}>Loading preview…</div>
      )}
      {(failed || (!isImage)) && (
        <div style={{
          height: 96, borderRadius: 8, background: 'rgba(15,27,70,0.85)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          color: '#94a3b8', fontSize: 12,
        }}>
          {failed ? <ImageOff size={16} /> : <FileText size={16} />}
          {failed ? 'Preview unavailable' : attachment.mime_type || 'File'}
        </div>
      )}
      <div style={{ fontSize: 12.5, color: '#f1f5f9', fontWeight: 700 }}>
        {attachment.filename}
      </div>
      {attachment.caption && (
        <div className="jbv2-row-note" style={{ fontSize: 12.5 }}>{attachment.caption}</div>
      )}
      <div className="jbv2-hp-row-meta" style={{ fontSize: 11 }}>
        {attachment.actor} · {attachment.created_at && new Date(attachment.created_at).toLocaleTimeString()}
        {' · '}{Math.max(1, Math.round((attachment.size || 0) / 1024))} KB
      </div>
    </div>
  );
}
